export function formatDate(date: Date): string {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`
}

/**
 * Render a reading timestamp (unix seconds) for display. Returns an empty
 * string when there is no reading, so callers can drop it into a label without
 * printing a bare "0".
 */
export function formatReadingTimestamp(timestampInSeconds: number): string {
  if (!timestampInSeconds) return ''
  return new Date(timestampInSeconds * 1000).toLocaleString()
}

export function range(start: number, stop: number, step: number) {
  if (typeof stop == 'undefined') {
    // one param defined
    stop = start
    start = 0
  }

  if (typeof step == 'undefined') {
    step = 1
  }

  if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
    return []
  }

  var result = []
  for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
    result.push(i)
  }

  return result
}

export function roundToDecimal(num: number, decimalPlaces: number = 1) {
  return Math.round(num * 10 ** decimalPlaces) / 10 ** decimalPlaces
}

// Group the integer part with thousands-separator commas while keeping the given
// decimal precision. Drop-in replacement for `.toFixed(precision)` that also adds
// commas (e.g. formatNumber(18060748.21, 2) => "18,060,748.21"). Small values are
// unaffected. Do NOT use for coordinates, IDs, versions, or CSV cells.
export function formatNumber(value: number, precision?: number): string {
  if (value === undefined || value === null || isNaN(value)) return ''
  return value.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision ?? 20,
  })
}

export function parseFirmwareVersion(value: number): string {
  const major = (value >> 24) & 0xff
  const minor = (value >> 16) & 0xff
  const micro = (value >> 8) & 0xff
  const beta = (value >> 0) & 0xff
  return beta === 0 ? `v${major}.${minor}.${micro}` : `v${major}.${minor}.${micro}b${beta}`
}

export function parseHardwareVersion(value: number): string {
  const major = (value >> 8) & 0x0ff
  const minor = (value >> 0) & 0x0ff
  return `v${major}.${minor}`
}

export function titleCase(str: string) {
  return str
    .toLowerCase()
    .split(' ')
    .map(function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

export function convertToSensorId(sensorPort: string): number {
  return (
    {
      A1: 1,
      A2: 2,
      B1: 3,
      B2: 4,
      C1: 5,
      C2: 6,
      C3: 7,
    }[sensorPort] || 0
  )
}

export function determinePrecision(value: number, digits: number) {
  let limit = 15
  let precision = 1
  let tempValue = value - Math.floor(value)

  if (value < 1) {
    while (limit--) {
      tempValue *= 10
      if (Math.floor(tempValue).toString().length === digits) {
        break
      }
      precision += 1
    }
  }

  return precision
}

export function breakUp<T>(items: T[], size: number): T[][] {
  return items.reduce((acc, item, i) => {
    const index = Math.floor(i / size)
    if (!acc[index]) {
      acc[index] = []
    }
    acc[index].push(item)
    return acc
  }, [] as T[][])
}

export function getDaysSinceEpoch(date: Date): number {
  return Math.floor(date.getTime() / (1000 * 60 * 60 * 24))
}
